import { useId, type ComponentPropsWithoutRef, type ReactNode, type Ref } from 'react';
import { cn } from '@/lib/cn';
import { useInterfaceLanguage } from '@/lib/interface-language';

/**
 * A collapsible section whose open/closed state is **controlled**, not
 * `<details>`/`<summary>` DOM state (`FE-04.10`;
 * `docs/design/stage2/card-back.md`'s `Disclosure` row, `FE-16.1`).
 *
 * `FR-CARD-034` requires the state to persist per user **across cards**, so
 * card back's own C3 finding stands: `user_preferences.activity_section_collapsed`
 * has a column and no route. This component takes `open`/`onOpenChange` as
 * plain controlled props and does not invent the persistence itself — the
 * caller wires `onOpenChange` to wherever that preference eventually lives
 * (in-memory today, a route later), per `B3-BRIEF.md`'s instruction to take
 * the preference as a prop rather than build storage for it.
 *
 * `card-back.md` also lists a `persistKey` prop; it is dropped here on
 * purpose — a key implies this component owns storage, which is exactly what
 * the brief says not to invent. See this chunk's report for the full note.
 *
 * `count` is what makes the collapsed summary honest — "Comments and
 * activity (12)" rather than a closed box with no information in it.
 */
export interface DisclosureProps extends Omit<
  ComponentPropsWithoutRef<'div'>,
  'onChange' | 'children'
> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  summary: string;
  count?: number;
  children: ReactNode;
  ref?: Ref<HTMLDivElement>;
}

export function Disclosure({
  open,
  onOpenChange,
  summary,
  count,
  children,
  className,
  ref,
  ...rest
}: DisclosureProps) {
  const { t } = useInterfaceLanguage();
  const contentId = useId();
  const label =
    count !== undefined
      ? t('primitives.disclosure.summaryWithCount', { summary, count }, count)
      : summary;

  return (
    <div ref={ref} className={cn(className)} {...rest}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={contentId}
        onClick={() => onOpenChange(!open)}
        className="flex w-full items-center justify-between gap-2 rounded-card py-2 text-left font-medium text-ink hover:bg-surface-raised"
      >
        <span>{label}</span>
        <ChevronGlyph className={cn('transition-transform', open && 'rotate-180')} />
      </button>

      {open && <div id={contentId}>{children}</div>}
    </div>
  );
}

function ChevronGlyph({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      aria-hidden="true"
      focusable="false"
      className={cn('size-4 shrink-0', className)}
    >
      <path
        d="M4 6l4 4 4-4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
